import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from "react";
import { getAll } from "../../services/reports.service";
import Header from "../../components/shared/header";
import "./common.css";
import { hasVisibleColumn } from "./helper";
import dayjs from "dayjs";
const CommonList = forwardRef(
    (
        {
            activeIndex,
            reportType = "",
            headerTemplate = undefined,
            rowGroupMode = undefined,
            groupRowsBy = undefined,
            additional = {},
            showHeader = true,
            queryParam = {},
            dataFormatter = undefined,
            cb = (paramObj) => {},
            complementaryPrepare = undefined,
            extendSummaryPrepare = undefined,
            extendSummaryPrepareOne = undefined,
            extendSummaryPrepareTwo = undefined,
        },
        ref
    ) => {
        const totalCountRef = useRef(0);
        const [reportsHeaders, setReportsHeaders] = useState([]);
        const [reportsBody, setReportsBody] = useState([]);
        const [reportsFooters, setReportsFooters] = useState([]);
        const [reportsExtendSummary, setReportsExtendSummary] = useState([]);
        const [reportsExtendSummaryOne, setReportsExtendSummaryOne] = useState(
            []
        );
        const [reportsComplementary, setReportsComplementary] = useState({});
        const [reportsExtendSummaryTwo, setReportsExtendSummaryTwo] = useState(
            []
        );
        const [reportsAdditionals, setReportsAdditionals] = useState({});
        const [tempReportType, setTempReportType] = useState("");
        const [pageTitle, setPageTitle] = useState("Reports");
        const [pagination, setPagination] = useState(null);
        const [loading, setLoading] = useState(false);
        const [lazyState, setLazyState] = useState({
            first: 0,
            rows: 10,
            page: 1,
            sortField: null,
            sortOrder: null,
        });
        const getParams = () => {
            const params = {
                page: lazyState.page,
                limit: lazyState.rows,
            };
            return params;
        };

        useEffect(() => {
            loadLazyData(tempReportType);
        }, [lazyState, activeIndex]);

        const resetData = () => {
            setReportsBody([]);
            setReportsHeaders([]);
            setReportsFooters([]);
            setReportsExtendSummary([]);
            setReportsExtendSummaryOne([]);
            setReportsComplementary([]);
            setReportsExtendSummaryTwo([]);
            setReportsAdditionals({});
            setPagination(null);
            setLazyState({
                first: 0,
                rows: 10,
                page: 1,
                sortField: null,
                sortOrder: null,
            });
            totalCountRef.current = 0;
        };

        useImperativeHandle(ref, () => ({
            updatelazyData(customReportType, queryParam, bodypayload) {
                setTempReportType(customReportType);
                loadLazyData(customReportType, queryParam, bodypayload);
            },
            resetData,
        }));

        const loadLazyData = (
            customReportType = null,
            propsQueryParam = {},
            bodyAdditional = {}
        ) => {
            setLoading(true);
            const params = getParams();
            getAll(
                customReportType || reportType,
                { ...params, ...queryParam, ...propsQueryParam },
                { ...additional, ...bodyAdditional }
            )
                .then((data) => {
                    setPageTitle(
                        data?.data?.data?.title ?? data?.data?.data?.data?.title
                    );
                    setReportsHeaders(
                        data?.data?.data?.headers ??
                            data?.data?.data?.data?.headers
                    );
                    setReportsFooters(
                        data?.data?.data?.footers ??
                            data?.data?.data?.data?.footers ??
                            []
                    );
                    setReportsExtendSummary(
                        data?.data?.data?.extendSummary ??
                            data?.data?.data?.data?.extendSummary ??
                            []
                    );
                    setReportsExtendSummaryOne(
                        data?.data?.data?.extendSummaryOne ??
                            data?.data?.data?.data?.extendSummaryOne ??
                            []
                    );
                    setReportsComplementary({
                        complementaryHeaders:
                            data?.data?.data?.complementaryHeaders || [],
                        complementaryData:
                            data?.data?.data?.complementaryData || [],
                    });
                    setReportsExtendSummaryTwo(
                        data?.data?.data?.extendSummaryTwo ??
                            data?.data?.data?.data?.extendSummaryTwo ??
                            []
                    );
                    setReportsAdditionals(data?.data?.data?.additionals || {});
                    let bodyData =
                        data?.data?.data?.data || data?.data?.data?.items || [];
                    cb({ bodyCount: bodyData.length });
                    setReportsBody(
                        dataFormatter
                            ? dataFormatter(
                                  bodyData,
                                  lazyState.page,
                                  lazyState.rows
                              )
                            : bodyData
                    );
                    setPagination({
                        current_page: data?.data?.data?.meta?.currentPage,
                        per_page: data?.data?.data?.meta?.perPage,
                        next: data?.data?.data?.meta?.nextPageUrl,
                        count: data?.data?.data?.meta?.total,
                    });
                    totalCountRef.current =
                        data?.data?.data?.meta?.total || totalCountRef.current;
                    setLoading(false);
                })
                .catch((e) => {
                    setLoading(false);
                });
        };

        const onPage = (event) => {
            event["page"] = event["page"] + 1;
            setLazyState(event);
        };

        const onSort = (event) => {
            setLazyState(event);
        };

        const onFilter = (event) => {
            event["first"] = 0;
            setLazyState(event);
        };

        const columnStyle = {
            fontSize: "13px",
            padding: "4px 2px 4px 2px",
        };

        const rowClassName = (rowData) => {
            if (!reportsAdditionals?.row_styles?.status) return {};
            return {
                "status-yellow":
                    rowData?.status === "migrated" &&
                    reportsAdditionals?.row_styles?.status?.includes(
                        "migrated"
                    ),
                "status-red":
                    (rowData?.status === "cancelled" &&
                        reportsAdditionals?.row_styles?.status?.includes(
                            "cancelled"
                        )) ||
                    (rowData?.status === "refunded" &&
                        reportsAdditionals?.row_styles?.status?.includes(
                            "refunded"
                        )),
            };
        };
        return (
            <>
                {pageTitle && showHeader && (
                    <Header
                        items={[{ label: pageTitle }]}
                        pageTitle={pageTitle}
                    />
                )}
                <DataTable
                    rowGroupHeaderTemplate={headerTemplate}
                    rowGroupMode={rowGroupMode}
                    groupRowsBy={groupRowsBy}
                    paginator={totalCountRef.current ? true : false}
                    first={lazyState.first ?? null}
                    rows={lazyState.rows ?? 10}
                    rowClassName={rowClassName}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    totalRecords={pagination?.count ?? totalCountRef.current}
                    onPage={onPage}
                    value={reportsBody}
                    lazy
                    filterDisplay="row"
                    dataKey="id"
                    onSort={onSort}
                    loading={loading}
                    scrollable={true}
                    scrollHeight="750px"
                >
                    {reportsHeaders?.map((header) => {
                        let userSearch = { ...additional, ...queryParam };
                        let hasExceptionSource = false;
                        if (header?.exception_source?.length) {
                            hasExceptionSource = Object.keys(userSearch)?.find(
                                (item) =>
                                    header?.exception_source
                                        .split("|")
                                        .includes(item)
                            );
                        }
                        let showCol = hasVisibleColumn(header, {
                            ...additional,
                            ...queryParam,
                        });
                        let exceptionData = null;
                        if (
                            header?.type == "date" &&
                            userSearch?.[hasExceptionSource]
                        ) {
                            exceptionData = dayjs(
                                userSearch?.[hasExceptionSource]
                            ).format("DD-MM-YYYY");
                        } else if (userSearch?.[hasExceptionSource]) {
                            exceptionData = userSearch?.[hasExceptionSource];
                        }
                        return (
                            showCol && (
                                <Column
                                    body={(value) =>
                                        header?.type &&
                                        header.type == "number" &&
                                        !isNaN(value?.[header?.key])
                                            ? new Intl.NumberFormat("en-IN", {
                                                  style: "decimal",
                                              }).format(
                                                  Number.parseInt(
                                                      value?.[header?.key]
                                                  )
                                              )
                                            : hasExceptionSource &&
                                              exceptionData
                                            ? exceptionData
                                            : value?.[header?.key]
                                    }
                                    // footer={
                                    //     pickFooter &&
                                    //     pickFooter?.type == "string"
                                    //         ? ` ${pickFooter?.data}`
                                    //         : pickFooter
                                    //         ? `${
                                    //               !isNaN(pickFooter?.data)
                                    //                   ? new Intl.NumberFormat(
                                    //                         "en-IN",
                                    //                         {
                                    //                             style: "decimal",
                                    //                         }
                                    //                     ).format(
                                    //                         pickFooter?.data
                                    //                     )
                                    //                   : pickFooter.value
                                    //           }`
                                    //         : ""
                                    // }
                                    bodyStyle={{
                                        textAlign:
                                            header?.type &&
                                            header?.type == "number"
                                                ? "right"
                                                : "center",
                                        border: "1px solid #d7d2d2",
                                    }}
                                    headerClassName="text-center"
                                    headerStyle={{
                                        borderBottom: "0px",
                                        textAlign: "center",
                                    }}
                                    footerStyle={{
                                        textAlign: "right",
                                    }}
                                    header={header?.value}
                                    filterPlaceholder=""
                                    showFilterMenu={false}
                                    style={columnStyle}
                                />
                            )
                        );
                    })}
                </DataTable>

                {reportsHeaders?.length ? (
                    <div className="custom-footer">
                        <table className="footer-table">
                            <thead>
                                <tr>
                                    {reportsHeaders?.map((header) => {
                                        let showCol = hasVisibleColumn(header, {
                                            ...additional,
                                            ...queryParam,
                                        });
                                        let pickFooter = reportsFooters.find(
                                            (item) => item.key === header.key
                                        );
                                        return pickFooter && showCol ? (
                                            <td
                                                key={header.key}
                                                className="footer-cell"
                                            >
                                                {pickFooter.value === "Summary"
                                                    ? ""
                                                    : header.value}
                                            </td>
                                        ) : null;
                                    })}
                                </tr>
                            </thead>
                            <tfoot>
                                <tr>
                                    {reportsHeaders?.map((header) => {
                                        let pickFooter = reportsFooters.find(
                                            (item) => item.key === header.key
                                        );
                                        let showCol = hasVisibleColumn(header, {
                                            ...additional,
                                            ...queryParam,
                                        });
                                        if (pickFooter?.value === "Summary") {
                                            return (
                                                <td
                                                    key={header.key}
                                                    className="footer-cell"
                                                >
                                                    {pickFooter.value}
                                                </td>
                                            );
                                        }
                                        return pickFooter && showCol ? (
                                            <td
                                                key={header.key}
                                                className="footer-cell"
                                            >
                                                {pickFooter?.type == "string"
                                                    ? ` ${pickFooter?.data}`
                                                    : pickFooter
                                                    ? `${
                                                          pickFooter?.description
                                                              ? `${pickFooter?.description}-`
                                                              : ""
                                                      } ${
                                                          !isNaN(
                                                              pickFooter?.data
                                                          )
                                                              ? new Intl.NumberFormat(
                                                                    "en-IN",
                                                                    {
                                                                        style: "decimal",
                                                                    }
                                                                ).format(
                                                                    Number.parseInt(
                                                                        pickFooter?.data
                                                                    )
                                                                )
                                                              : pickFooter.value
                                                      }`
                                                    : ""}
                                            </td>
                                        ) : null;
                                    })}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : null}

                {extendSummaryPrepareOne !== undefined
                    ? extendSummaryPrepareOne(reportsExtendSummaryOne)
                    : null}

                {complementaryPrepare != undefined
                    ? complementaryPrepare(reportsComplementary)
                    : null}
                {extendSummaryPrepare !== undefined
                    ? extendSummaryPrepare(reportsExtendSummary)
                    : Boolean(reportsExtendSummary?.length) && (
                          <table className="mt-5 bg-white p-2">
                              {reportsExtendSummary?.map((item, i) => {
                                  let showCol = hasVisibleColumn(item, {
                                      ...additional,
                                      ...queryParam,
                                  });
                                  return showCol ? (
                                      <tr>
                                          {item?.type == "summary_heading" ? (
                                              <td
                                                  colSpan={2}
                                                  style={{
                                                      background: "#bab2b2",
                                                      fontSize: "18px",
                                                      fontWeight:
                                                          i == 0 ? "bold" : "",
                                                      padding:
                                                          "0px 10px 0px 10px",
                                                  }}
                                              >
                                                  {item.value}
                                              </td>
                                          ) : (
                                              <>
                                                  <td
                                                      style={{
                                                          border: "1px solid #d7d2d2",
                                                          fontSize: "18px",
                                                          fontWeight:
                                                              i == 0
                                                                  ? "bold"
                                                                  : "",
                                                          padding:
                                                              "0px 10px 0px 10px",
                                                      }}
                                                  >
                                                      {item.value}
                                                  </td>
                                                  <td
                                                      style={{
                                                          border: "1px solid #d7d2d2",
                                                          fontSize: "18px",
                                                          fontWeight:
                                                              i == 0
                                                                  ? "bold"
                                                                  : "",
                                                          padding:
                                                              "0px 10px 0px 10px",
                                                      }}
                                                  >
                                                      {new Intl.NumberFormat(
                                                          "en-IN",
                                                          {
                                                              style: "decimal",
                                                          }
                                                      ).format(item.data)}
                                                  </td>
                                              </>
                                          )}
                                      </tr>
                                  ) : null;
                              })}
                          </table>
                      )}
                {extendSummaryPrepareTwo !== undefined
                    ? extendSummaryPrepareTwo(reportsExtendSummaryTwo)
                    : null}
            </>
        );
    }
);

export default CommonList;
